import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  BookingCellId,
  bulkApprovalBookingConflictsAtom,
  bulkApprovalBookingConflictsUpdatedAtom,
  BulkBookingCreateConflict,
  BulkCreateBookingState,
  bulkCreateBookingStateAtom,
  getApprovalBookingConflictFromConflict,
  getBookingCalendarCellId,
  IApprovalBookingConflict,
} from "@ryde/shared";

interface IApprovalBookingConflictsFromConflictErrorArguments {
  conflict: BulkBookingCreateConflict;
  approvalBookingConflictsMap: Map<BookingCellId, IApprovalBookingConflict[]>;
}

const approvalBookingConflictsFromConflictError = ({
  approvalBookingConflictsMap,
  conflict,
}: IApprovalBookingConflictsFromConflictErrorArguments): Map<
  BookingCellId,
  IApprovalBookingConflict[]
> => {
  if (!conflict.passengerId) {
    return new Map([...approvalBookingConflictsMap]);
  }

  const cellId = getBookingCalendarCellId(
    Number(conflict.passengerId),
    conflict.date,
  );

  if (approvalBookingConflictsMap.has(cellId)) {
    const prevApprovalBookingConflicts = approvalBookingConflictsMap.get(
      cellId,
    ) as IApprovalBookingConflict[];

    const newApprovalBookingConflicts = [
      ...prevApprovalBookingConflicts.filter(c => c.id !== conflict.id),
      getApprovalBookingConflictFromConflict(conflict),
    ];

    return new Map([
      ...approvalBookingConflictsMap.set(
        getBookingCalendarCellId(Number(conflict.passengerId), conflict.date),
        newApprovalBookingConflicts,
      ),
    ]);
  }

  return new Map([
    ...approvalBookingConflictsMap.set(cellId, [
      getApprovalBookingConflictFromConflict(conflict),
    ]),
  ]);
};

export const useBulkPreviewStep = () => {
  const bulkCreateBookingState = useRecoilValue(bulkCreateBookingStateAtom);
  const setApprovalBookingConflicts = useSetRecoilState(
    bulkApprovalBookingConflictsAtom,
  );

  const setBulkApprovalBookingConflictsUpdated = useSetRecoilState(
    bulkApprovalBookingConflictsUpdatedAtom,
  );

  const resolveApprovalBookingConflicts = useEvent(
    ({ conflictError }: { conflictError: BulkBookingCreateConflict }) => {
      if (bulkCreateBookingState === BulkCreateBookingState.IsFirstPreview) {
        setApprovalBookingConflicts(prevState =>
          approvalBookingConflictsFromConflictError({
            approvalBookingConflictsMap: prevState,
            conflict: conflictError,
          }),
        );
        setBulkApprovalBookingConflictsUpdated(prevState =>
          approvalBookingConflictsFromConflictError({
            approvalBookingConflictsMap: prevState,
            conflict: conflictError,
          }),
        );
      }

      if (bulkCreateBookingState === BulkCreateBookingState.IsPreview) {
        setBulkApprovalBookingConflictsUpdated(prevState =>
          approvalBookingConflictsFromConflictError({
            approvalBookingConflictsMap: prevState,
            conflict: conflictError,
          }),
        );
      }
    },
  );

  return { resolveApprovalBookingConflicts };
};
